/**
 * SRE Surface Canvas V3 - With Auto Detection
 * ============================================
 * 
 * ULTIMATE UX: Auto-detect surfaces + Live preview
 * 
 * Features:
 * - AUTO SURFACE DETECTION on image load (WOW moment)
 * - Accept / Adjust / Redefine workflow
 * - 4-point click-to-define for manual mode
 * - LIVE PREVIEW of slab texture
 * - Dark overlay outside selected area
 * - Draggable corner handles
 * - Ghost/semi-transparent preview mode
 * 
 * NO AI GENERATIVA - Pure deterministic CV detection
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, Check, Edit3, X, Wand2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Corner configuration
const CORNERS = [
  { id: 0, label: 'TL', name: 'Top-Left' },
  { id: 1, label: 'TR', name: 'Top-Right' },
  { id: 2, label: 'BR', name: 'Bottom-Right' },
  { id: 3, label: 'BL', name: 'Bottom-Left' },
];

const SurfaceCanvasV3 = ({
  environmentImageUrl,
  environmentImageId, // For auto-detection API call
  slabImageUrl,
  initialPoints = null,
  onPointsChange,
  width = 900,
  height = 550,
  editable = true,
  previewOpacity = 0.85,
  autoDetect = true,
}) => {
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  
  // State
  const [points, setPoints] = useState(initialPoints || []);
  const [isComplete, setIsComplete] = useState(initialPoints?.length === 4);
  const [dragIndex, setDragIndex] = useState(null);
  const [environmentImage, setEnvironmentImage] = useState(null);
  const [slabImage, setSlabImage] = useState(null);
  const [imageScale, setImageScale] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [hoveredCorner, setHoveredCorner] = useState(null);
  
  // Auto-detection state
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedSurfaces, setDetectedSurfaces] = useState([]);
  const [selectedSurfaceIndex, setSelectedSurfaceIndex] = useState(0);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [detectionMessage, setDetectionMessage] = useState('');
  const [manualMode, setManualMode] = useState(false);
  
  // Load environment image
  useEffect(() => {
    if (!environmentImageUrl) return;
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setEnvironmentImage(img);
      
      // Calculate scale to fit canvas
      const scaleX = width / img.width;
      const scaleY = height / img.height;
      const scale = Math.min(scaleX, scaleY);
      setImageScale(scale);
      
      // Calculate offset to center
      const offsetX = (width - img.width * scale) / 2;
      const offsetY = (height - img.height * scale) / 2;
      setImageOffset({ x: offsetX, y: offsetY });
    };
    img.src = environmentImageUrl;
  }, [environmentImageUrl, width, height]);
  
  // Load slab image
  useEffect(() => {
    if (!slabImageUrl) return;
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setSlabImage(img);
    img.src = slabImageUrl;
  }, [slabImageUrl]);
  
  // Auto-detect surfaces when image loads
  useEffect(() => {
    if (!autoDetect || !environmentImage || !environmentImageId || initialPoints?.length === 4) return;
    if (manualMode) return;
    
    const detectSurfaces = async () => {
      setIsDetecting(true);
      setDetectionMessage('Analyzing image...');
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/sre/detect-surfaces`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_id: environmentImageId,
            max_results: 3
          })
        });
        
        const data = await response.json();
        
        if (data.success && data.surfaces && data.surfaces.length > 0) {
          // Scale detected points to canvas coordinates
          const scaledSurfaces = data.surfaces.map(surf => ({
            ...surf,
            points: surf.points.map(p => [
              Math.round(p[0] * imageScale + imageOffset.x),
              Math.round(p[1] * imageScale + imageOffset.y)
            ])
          }));
          
          setDetectedSurfaces(scaledSurfaces);
          setSelectedSurfaceIndex(0);
          setShowSuggestion(true);
          
          // Auto-apply first suggestion
          const firstSurface = scaledSurfaces[0];
          setPoints(firstSurface.points);
          setIsComplete(true);
          
          setDetectionMessage(`Found ${scaledSurfaces.length} surface(s) - ${Math.round(firstSurface.confidence * 100)}% confidence`);
          
          // Notify parent
          if (onPointsChange) {
            onPointsChange(formatPoints(firstSurface.points));
          }
        } else {
          setDetectionMessage(data.message || 'No surfaces detected');
          setManualMode(true);
        }
      } catch (err) {
        console.error('Surface detection failed:', err);
        setDetectionMessage('Detection unavailable - define manually');
        setManualMode(true);
      } finally {
        setIsDetecting(false);
      }
    };
    
    // Small delay to ensure image is fully rendered
    const timer = setTimeout(detectSurfaces, 500);
    return () => clearTimeout(timer);
  }, [autoDetect, environmentImage, environmentImageId, imageScale, imageOffset, initialPoints, manualMode, onPointsChange]);
  
  // Format points for export
  const formatPoints = useCallback((pts) => {
    return {
      points: pts,
      canvas_dimensions: { width, height },
      image_scale: imageScale,
      image_offset: imageOffset,
    };
  }, [width, height, imageScale, imageOffset]);
  
  // Main render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    
    if (!canvas || !overlayCanvas || !previewCanvas) return;
    
    const ctx = canvas.getContext('2d');
    const overlayCtx = overlayCanvas.getContext('2d');
    const previewCtx = previewCanvas.getContext('2d');
    
    // Clear all canvases
    ctx.clearRect(0, 0, width, height);
    overlayCtx.clearRect(0, 0, width, height);
    previewCtx.clearRect(0, 0, width, height);
    
    // Fill background
    ctx.fillStyle = '#0A0A0A';
    ctx.fillRect(0, 0, width, height);
    
    // Draw environment image
    if (environmentImage) {
      ctx.drawImage(
        environmentImage,
        imageOffset.x,
        imageOffset.y,
        environmentImage.width * imageScale,
        environmentImage.height * imageScale
      );
    }
    
    // Draw dark overlay with cutout for selected surface
    if (points.length === 4) {
      overlayCtx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      overlayCtx.fillRect(0, 0, width, height);
      
      // Cut out the selected area
      overlayCtx.globalCompositeOperation = 'destination-out';
      overlayCtx.beginPath();
      overlayCtx.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < 4; i++) {
        overlayCtx.lineTo(points[i][0], points[i][1]);
      }
      overlayCtx.closePath();
      overlayCtx.fill();
      overlayCtx.globalCompositeOperation = 'source-over';
      
      // Draw live preview of slab texture
      if (slabImage) {
        renderSlabPreview(previewCtx);
      }
    }
    
    // Draw surface boundary
    if (points.length > 0) {
      drawSurfaceBoundary(ctx);
    }
    
    // Draw corner handles
    drawCornerHandles(ctx);
    
  }, [environmentImage, slabImage, points, imageOffset, imageScale, width, height, previewOpacity]);
  
  // Render slab preview with perspective transform
  const renderSlabPreview = useCallback((ctx) => {
    if (!slabImage || points.length !== 4) return;
    
    ctx.save();
    
    // Create clipping path
    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < 4; i++) {
      ctx.lineTo(points[i][0], points[i][1]);
    }
    ctx.closePath();
    ctx.clip();
    
    // Calculate bounding box
    const minX = Math.min(...points.map(p => p[0]));
    const maxX = Math.max(...points.map(p => p[0]));
    const minY = Math.min(...points.map(p => p[1]));
    const maxY = Math.max(...points.map(p => p[1]));
    
    const targetWidth = maxX - minX;
    const targetHeight = maxY - minY;
    
    // Tile the slab texture
    const tileWidth = Math.min(targetWidth, slabImage.width * 0.5);
    const tileHeight = tileWidth * (slabImage.height / slabImage.width);
    
    ctx.globalAlpha = previewOpacity;
    
    for (let y = minY; y < maxY + tileHeight; y += tileHeight) {
      for (let x = minX; x < maxX + tileWidth; x += tileWidth) {
        ctx.drawImage(slabImage, x, y, tileWidth, tileHeight);
      }
    }
    
    ctx.restore();
  }, [slabImage, points, previewOpacity]);
  
  // Draw surface boundary
  const drawSurfaceBoundary = useCallback((ctx) => {
    if (points.length === 0) return;
    
    ctx.save();
    
    // Draw filled polygon
    if (points.length >= 3) {
      ctx.beginPath();
      ctx.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i][0], points[i][1]);
      }
      if (points.length === 4) ctx.closePath();
      ctx.fillStyle = points.length === 4 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)';
      ctx.fill();
    }
    
    // Draw boundary
    ctx.strokeStyle = showSuggestion ? '#4ADE80' : (points.length === 4 ? '#FFFFFF' : '#A0A0A0');
    ctx.lineWidth = points.length === 4 ? 2 : 1;
    ctx.setLineDash(points.length === 4 ? [] : [5, 5]);
    
    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i][0], points[i][1]);
    }
    if (points.length === 4) ctx.closePath();
    ctx.stroke();
    
    ctx.restore();
  }, [points, showSuggestion]);
  
  // Draw corner handles
  const drawCornerHandles = useCallback((ctx) => {
    points.forEach((point, index) => {
      const isHovered = hoveredCorner === index;
      const isDragging = dragIndex === index;
      
      ctx.save();
      
      // Outer ring
      if (isHovered || isDragging) {
        ctx.beginPath();
        ctx.arc(point[0], point[1], 20, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fill();
      }
      
      // Main handle
      ctx.beginPath();
      ctx.arc(point[0], point[1], isDragging ? 14 : (isHovered ? 12 : 10), 0, Math.PI * 2);
      ctx.fillStyle = showSuggestion ? '#4ADE80' : '#FFFFFF';
      ctx.fill();
      
      // Inner dot
      ctx.beginPath();
      ctx.arc(point[0], point[1], 4, 0, Math.PI * 2);
      ctx.fillStyle = '#121212';
      ctx.fill();
      
      // Label
      ctx.font = 'bold 10px system-ui';
      ctx.fillStyle = '#A0A0A0';
      ctx.textAlign = 'center';
      ctx.fillText(CORNERS[index].label, point[0], point[1] - 20);
      
      ctx.restore();
    });
  }, [points, hoveredCorner, dragIndex, showSuggestion]);
  
  // Render on changes
  useEffect(() => {
    render();
  }, [render]);
  
  // Get corner at point
  const getCornerAtPoint = useCallback((x, y) => {
    const hitRadius = 20;
    for (let i = 0; i < points.length; i++) {
      const dx = x - points[i][0];
      const dy = y - points[i][1];
      if (Math.sqrt(dx * dx + dy * dy) < hitRadius) {
        return i;
      }
    }
    return null;
  }, [points]);
  
  // Handle mouse down
  const handleMouseDown = useCallback((e) => {
    if (!editable) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const cornerIndex = getCornerAtPoint(x, y);
    
    if (cornerIndex !== null) {
      setDragIndex(cornerIndex);
      setShowSuggestion(false); // User is adjusting
    } else if (points.length < 4 && !showSuggestion) {
      // Manual mode - add new point
      const newPoints = [...points, [Math.round(x), Math.round(y)]];
      setPoints(newPoints);
      
      if (newPoints.length === 4) {
        setIsComplete(true);
        if (onPointsChange) {
          onPointsChange(formatPoints(newPoints));
        }
      }
    }
  }, [editable, points, getCornerAtPoint, showSuggestion, onPointsChange, formatPoints]);
  
  // Handle mouse move
  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (dragIndex !== null) {
      const newPoints = [...points];
      newPoints[dragIndex] = [Math.round(x), Math.round(y)];
      setPoints(newPoints);
      
      if (onPointsChange) {
        onPointsChange(formatPoints(newPoints));
      }
    } else {
      setHoveredCorner(getCornerAtPoint(x, y));
    }
  }, [dragIndex, points, getCornerAtPoint, onPointsChange, formatPoints]);
  
  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (dragIndex !== null) {
      if (onPointsChange) {
        onPointsChange(formatPoints(points));
      }
      setDragIndex(null);
    }
  }, [dragIndex, points, onPointsChange, formatPoints]);
  
  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoveredCorner(null);
    if (dragIndex !== null) setDragIndex(null);
  }, [dragIndex]);
  
  // Accept suggestion
  const handleAcceptSuggestion = useCallback(() => {
    setShowSuggestion(false);
    setManualMode(false);
  }, []);
  
  // Switch to manual mode
  const handleManualMode = useCallback(() => {
    setShowSuggestion(false);
    setManualMode(true);
    setPoints([]);
    setIsComplete(false);
    if (onPointsChange) {
      onPointsChange(null);
    }
  }, [onPointsChange]);
  
  // Reset
  const handleReset = useCallback(() => {
    setPoints([]);
    setIsComplete(false);
    setDragIndex(null);
    setShowSuggestion(false);
    setDetectedSurfaces([]);
    setManualMode(false);
    if (onPointsChange) {
      onPointsChange(null);
    }
  }, [onPointsChange]);
  
  // Cycle through detected surfaces
  const handleCycleSurface = useCallback(() => {
    if (detectedSurfaces.length <= 1) return;
    
    const nextIndex = (selectedSurfaceIndex + 1) % detectedSurfaces.length;
    setSelectedSurfaceIndex(nextIndex);
    
    const surface = detectedSurfaces[nextIndex];
    setPoints(surface.points);
    setDetectionMessage(`Surface ${nextIndex + 1}/${detectedSurfaces.length} - ${Math.round(surface.confidence * 100)}% confidence`);
    
    if (onPointsChange) {
      onPointsChange(formatPoints(surface.points));
    }
  }, [detectedSurfaces, selectedSurfaceIndex, onPointsChange, formatPoints]);
  
  return (
    <div className="relative" data-testid="sre-surface-canvas-v3">
      {/* Stacked canvases */}
      <div 
        className="relative rounded-lg overflow-hidden border border-[#2A2A2A]"
        style={{ width, height }}
      >
        {/* Base: Environment */}
        <canvas ref={canvasRef} width={width} height={height} className="absolute inset-0" style={{ zIndex: 1 }} />
        
        {/* Preview: Slab texture */}
        <canvas ref={previewCanvasRef} width={width} height={height} className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }} />
        
        {/* Overlay: Dark mask */}
        <canvas ref={overlayCanvasRef} width={width} height={height} className="absolute inset-0 pointer-events-none" style={{ zIndex: 3 }} />
        
        {/* Interaction layer */}
        <div
          className="absolute inset-0"
          style={{ 
            zIndex: 4,
            cursor: dragIndex !== null ? 'grabbing' : (hoveredCorner !== null ? 'grab' : 'crosshair')
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        />
        
        {/* Loading overlay */}
        {isDetecting && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] px-6 py-4 rounded-lg flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-[#D6D6D6] animate-spin" />
              <span className="text-white">Detecting surfaces...</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Status indicator */}
      <div className="absolute top-4 left-4 z-10">
        {isDetecting ? null : showSuggestion ? (
          // Suggestion mode
          <div className="bg-[#1A1A1A] border border-[#4ADE80]/30 px-4 py-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2 text-[#4ADE80] text-sm font-medium">
              <Wand2 className="w-4 h-4" />
              Suggested Surface
            </div>
            <div className="text-xs text-[#A0A0A0] mt-1 mb-3">{detectionMessage}</div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleAcceptSuggestion}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#4ADE80] hover:bg-[#22C55E] text-[#121212] text-xs font-medium rounded transition-colors"
                data-testid="sre-accept-surface"
              >
                <Check className="w-3 h-3" />
                Accept
              </button>
              {detectedSurfaces.length > 1 && (
                <button
                  onClick={handleCycleSurface}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white text-xs font-medium rounded transition-colors"
                >
                  Next ({selectedSurfaceIndex + 1}/{detectedSurfaces.length})
                </button>
              )}
              <button
                onClick={handleManualMode}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white text-xs font-medium rounded transition-colors"
                data-testid="sre-manual-mode"
              >
                <Edit3 className="w-3 h-3" />
                Manual
              </button>
            </div>
          </div>
        ) : !isComplete ? (
          // Manual mode - defining points
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] px-4 py-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2 text-white text-sm">
              <span className="w-2 h-2 bg-[#D6D6D6] rounded-full animate-pulse" />
              {points.length === 0 && 'Click to place Top-Left corner'}
              {points.length === 1 && 'Click to place Top-Right corner'}
              {points.length === 2 && 'Click to place Bottom-Right corner'}
              {points.length === 3 && 'Click to place Bottom-Left corner'}
            </div>
            <div className="text-xs text-[#666666] mt-1">
              Points: {points.length}/4 • {manualMode ? 'Manual mode' : 'Detection unavailable'}
            </div>
          </div>
        ) : (
          // Complete
          <div className="bg-[#1A1A1A] border border-[#4ADE80]/30 px-4 py-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2 text-[#4ADE80] text-sm">
              <Check className="w-4 h-4" />
              Surface defined
            </div>
            <div className="text-xs text-[#A0A0A0] mt-1">
              Drag corners to adjust • Ready to generate
            </div>
          </div>
        )}
      </div>
      
      {/* Reset button */}
      {editable && points.length > 0 && !showSuggestion && (
        <button
          onClick={handleReset}
          className="absolute top-4 right-4 z-10 px-3 py-1.5 bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#2A2A2A] text-[#A0A0A0] hover:text-white rounded text-xs font-medium transition-colors"
          data-testid="sre-reset-surface"
        >
          Reset
        </button>
      )}
      
      {/* Corner coordinates */}
      {points.length > 0 && (
        <div className="mt-4 grid grid-cols-4 gap-2">
          {points.map((point, index) => (
            <div
              key={index}
              className={`
                flex items-center gap-2 px-3 py-2 rounded text-xs
                ${dragIndex === index 
                  ? 'bg-[#2A2A2A] border border-[#D6D6D6]' 
                  : 'bg-[#1A1A1A] border border-[#2A2A2A]'
                }
              `}
            >
              <span className={`w-3 h-3 rounded-full ${showSuggestion ? 'bg-[#4ADE80]' : 'bg-white'}`} />
              <span className="text-[#666666]">{CORNERS[index].label}</span>
              <span className="text-white font-mono text-[10px]">({point[0]}, {point[1]})</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Live preview indicator */}
      {isComplete && slabImage && (
        <div className="mt-4 p-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-xs text-[#A0A0A0] flex items-center gap-2">
          <span className="w-2 h-2 bg-[#4ADE80] rounded-full animate-pulse" />
          Live preview active — Slab anchored to surface
        </div>
      )}
    </div>
  );
};

export default SurfaceCanvasV3;
